import { ChangeEvent, useEffect, useState } from 'react';
import { Alert } from '@mui/material';
import { CoreBox, CoreDialogContent, CoreFormControlLabel, CoreCheckbox, CoreTypography } from '@youscience/core';

import { SchoolSurveyData } from '@interfaces/favoriteSurvey';

import DialogFooter from '@components/DialogFooter';
import { ShareInfo } from '@components/OptIn/components/ShareInfo';

import useOptInStore from '@store/OptInStore';
import useNotificationStore from '@store/NotificationStore';

import { sxStyles, StyledDialog } from './OptInConnectionConfirmation.styles';

interface OptInConnectionConfirmationProps {
  schoolInfo: SchoolSurveyData;
  initialShareStatus: boolean;
  isShowConnectionConfirmation: boolean;
  setIsShowConnectionConfirmation: (status: boolean) => void;
  setIsSchoolConnected: (status: boolean) => void;
}

export const OptInConnectionConfirmation = (props: OptInConnectionConfirmationProps) => {
  const {
    schoolInfo: { id, name },
    isShowConnectionConfirmation,
    initialShareStatus,
    setIsShowConnectionConfirmation,
    setIsSchoolConnected,
  } = props;

  const saveUserConnection = useOptInStore((state) => state.saveUserConnection);
  const notify = useNotificationStore((state) => state.notify);

  const [isShare, setIsShare] = useState(initialShareStatus);
  const [isOpenShareInfo, setIsOpenShareInfo] = useState(false);

  const handleCheckBoxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsShare(checked);
  };

  const handleOpenShareInfo = () => {
    setIsShowConnectionConfirmation(false);

    setIsOpenShareInfo(true);
  };

  const handleCloseShareInfo = () => {
    setIsOpenShareInfo(false);

    setIsShowConnectionConfirmation(true);
  };

  const handleCancelOptInConnection = () => {
    setIsShowConnectionConfirmation(false);

    setIsSchoolConnected(false);
  };

  const handleSaveOptInConnection = () => {
    if (isShare) {
      saveUserConnection(id, isShare);

      notify({
        title: `Your data now shared with ${name}`,
        message: 'You can change permission all the time in the profile menu or on the detailed school page.',
        severity: 'success',
      });
    }

    setIsShowConnectionConfirmation(false);

    setIsSchoolConnected(isShare);
  };

  useEffect(() => {
    setIsShare(initialShareStatus);
  }, [initialShareStatus]);

  return (
    <>
      <StyledDialog data-testid="optInConnectionConfirmation" open={isShowConnectionConfirmation}>
        <CoreDialogContent sx={sxStyles.content}>
          <CoreBox sx={sxStyles.titles}>
            <CoreTypography data-testid="title" variant="h4" component="h3">{`Connect with ${name}`}</CoreTypography>

            <CoreTypography data-testid="subtitle">
              {`Would you like us to share your information with ${name} so they can contact you about their programs?`}
            </CoreTypography>
          </CoreBox>

          <CoreBox sx={sxStyles.formWrap}>
            <CoreFormControlLabel
              control={
                <CoreCheckbox data-testid="optInShareCheckBox" checked={isShare} onChange={handleCheckBoxChange} />
              }
              label={`Yes!  Please share my information with ${name}.`}
            />

            <Alert data-testid="infoMessage" sx={sxStyles.alert} severity="info">
              Please note that we will notify you once we have shared your information with the school, and that sharing
              your information with the school does not guarantee they will contact you. Remember to always double-check
              the college admission website for the most up-to-date information on application deadlines, campus visits,
              and financial aid.
            </Alert>
          </CoreBox>

          <CoreTypography data-testid="shareInfoText" sx={sxStyles.shareInfo} component="div" variant="caption">
            To see a list of the information we may share,{' '}
            <CoreTypography data-testid="shareInfoBtn" variant="caption" onClick={handleOpenShareInfo}>
              click here
            </CoreTypography>
            . Participating schools may compensate YouScience for this service, but there is no cost to you. You can opt
            out of sharing at any time by visiting your profile menu.
          </CoreTypography>
        </CoreDialogContent>

        <DialogFooter
          goBackButtonTitle="Cancel"
          goNextButtonTitle="Save"
          goNextActionButtonTitle="Saving"
          onGoBack={handleCancelOptInConnection}
          onGoNext={handleSaveOptInConnection}
        />
      </StyledDialog>

      {isOpenShareInfo && <ShareInfo isOpenShareInfo={isOpenShareInfo} onCloseShareInfo={handleCloseShareInfo} />}
    </>
  );
};
